import useStoreProfile from './useStoreProfile';
import PageHeader from '../../../../shared/components/layout/PageHeader';
import StoreLocationEditor from '../../components/StoreLocationEditor';
import OperatingHoursSection from '../../../appointments/components/OperatingHoursSection';
import { GoogleMapsProvider } from '../../../../shared/providers/GoogleMapsProvider';
import './StoreProfile.css';

const CATEGORY_OPTIONS = [
  { value: 'RETAIL', label: 'Retail' },
  { value: 'SALON', label: 'Salon' },
  { value: 'CLINIC', label: 'Clinic' },
  { value: 'FOOD_COURT', label: 'Food Court' }
];

const SyncingDataLoader = () => (
  <div className="global-request-overlay" aria-live="polite" aria-label="Syncing data">
    <div className="global-request-loader">
      <span className="global-request-spinner" aria-hidden="true" />
      <p>Syncing data...</p>
    </div>
  </div>
);

const PROFILE_ENTRY_LIMITS = {
  signatureHighlights: 5,
  valueProps: 4
};

const PROFILE_ENTRY_MAX_LENGTH = 120;

const ProfileEntryListField = ({
  name,
  label,
  purpose,
  example,
  entries,
  maxItems,
  onEntryChange,
  onAddEntry,
  onRemoveEntry
}) => {
  const values = Array.isArray(entries) && entries.length > 0 ? entries : [''];
  const cleanCount = Array.isArray(entries) ? entries.filter((item) => String(item || '').trim()).length : 0;

  return (
    <div className="field store-profile-field-full store-profile-list-field">
      <div className="store-profile-field-heading">
        <span className="label">{label}</span>
        <span className="store-profile-field-purpose">{purpose}</span>
      </div>
      <div className="store-profile-entry-list">
        {values.map((value, index) => (
          <div className="store-profile-entry-row" key={`${name}-${index}`}>
            <input
              type="text"
              className="input store-profile-entry-input"
              value={value}
              maxLength={PROFILE_ENTRY_MAX_LENGTH}
              onChange={(event) => onEntryChange(name, index, event.target.value)}
              placeholder={index === 0 ? example : `Add another ${label.toLowerCase().replace(/s$/, '')}`}
            />
            <span className="store-profile-entry-count">
              {String(value || '').length}/{PROFILE_ENTRY_MAX_LENGTH}
            </span>
            <button
              type="button"
              className="btn ghost small store-profile-entry-remove"
              onClick={() => onRemoveEntry(name, index)}
              disabled={values.length === 1 && !String(value || '').trim()}
              aria-label={`Remove ${label.toLowerCase()} ${index + 1}`}
            >
              Remove
            </button>
          </div>
        ))}
      </div>
      <div className="store-profile-entry-footer">
        <button
          type="button"
          className="btn ghost small"
          onClick={() => onAddEntry(name, maxItems)}
          disabled={values.length >= maxItems}
        >
          Add point
        </button>
        <span className="store-profile-entry-helper">
          Add one point at a time. {cleanCount}/{maxItems} saved points.
        </span>
      </div>
    </div>
  );
};

const toInitial = (storeName = '') => {
  const trimmed = String(storeName || '').trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : 'S';
};

const StoreProfile = ({ request, storeToken, storeUuid, onToast }) => {
  const {
    loading,
    formData,
    isSaving,
    isUploading,
    isUploadingGallery,
    isUploadingCover,
    isDeletingImage,
    isDeletingCover,
    uploadedJustNow,
    profileImageUrl,
    coverImageUrl,
    galleryPendingItems,
    canSave,
    hasChanges,
    legacyDescriptionNotice,
    readiness,
    handleChange,
    handleTimeChange,
    handleProfileEntryChange,
    handleAddProfileEntry,
    handleRemoveProfileEntry,
    handleLocationChange,
    handleLocationValidationChange,
    handleImageUpload,
    handleDeleteImage,
    handleCoverImageUpload,
    handleDeleteCoverImage,
    handleGalleryUpload,
    handleRemoveGalleryImage,
    handleCancel,
    handleSave
  } = useStoreProfile({
    request,
    storeToken,
    storeUuid,
    onToast
  });

  if (loading) {
    return <SyncingDataLoader />;
  }

  const galleryItems = [
    ...(Array.isArray(galleryPendingItems) ? galleryPendingItems.map((item) => ({ url: item.url, isPreview: true })) : []),
    ...((Array.isArray(formData.galleryImages) ? formData.galleryImages : []).map((url) => ({ url, isPreview: false })))
  ];

  return (
    <section className="section store-profile-shell">
      <PageHeader
        title="Store Profile"
        description="Update your store details shown across Zopeo touchpoints."
      />
      <div className="card store-profile-card">
        <div className="store-profile-avatar-row">
          <div className="store-profile-avatar" aria-hidden="true">
            {profileImageUrl ? (
              <img
                src={profileImageUrl}
                alt="Store profile"
                className="store-profile-avatar-image"
              />
            ) : (
              toInitial(formData.storeName)
            )}
          </div>
          <div className="store-profile-avatar-copy">
            <h3>Store Avatar</h3>
            <p>
              {formData.imgUploaded
                ? 'Your profile image is active. Upload a new one any time.'
                : 'Upload your store logo or profile image.'}
            </p>
          </div>
          <div className="store-profile-avatar-actions">
            <input
              type="file"
              id="storeImageUpload"
              accept=".jpg,.jpeg,.png"
              className="store-profile-hidden-input"
              onChange={handleImageUpload}
            />
            <label
              htmlFor="storeImageUpload"
              className={`btn small ${formData.imgUploaded ? 'ghost' : 'primary'}${isUploading ? ' disabled' : ''}`}
              aria-disabled={isUploading}
            >
              {isUploading ? 'Uploading...' : formData.imgUploaded ? 'Change Photo' : 'Upload Photo'}
            </label>
            <span
              className={`store-profile-image-status ${
                isUploading ? 'uploading' : formData.imgUploaded ? 'uploaded' : 'missing'
              }`}
              role="status"
              aria-live="polite"
            >
              {isUploading
                ? 'Uploading image...'
                : uploadedJustNow
                  ? 'Image uploaded successfully.'
                  : formData.imgUploaded
                    ? 'Image is added for this store.'
                    : 'No image added yet.'}
            </span>
            <span className="store-profile-upload-hint">Max 1MB · JPG / PNG</span>
            {formData.imgUploaded && (
              <button
                type="button"
                className={`btn ghost small danger${isDeletingImage ? ' disabled' : ''}`}
                onClick={handleDeleteImage}
                disabled={isDeletingImage || isUploading}
                aria-label="Remove profile image"
              >
                {isDeletingImage ? 'Removing...' : 'Remove Photo'}
              </button>
            )}
          </div>
        </div>

      </div>
      <div className="card store-profile-card">
        <div className="store-profile-cover-section">
          <div className="store-profile-gallery-header">
            <div>
              <h3>Store Cover Photo</h3>
              <p>Use a wide image for the Store Detail page hero.</p>
            </div>
          </div>
          <div className="store-profile-cover-preview" aria-label="Store cover photo preview">
            {coverImageUrl ? (
              <img src={coverImageUrl} alt="Store cover" className="store-profile-cover-image" />
            ) : (
              <div className="store-profile-cover-empty">
                <span>{toInitial(formData.storeName)}</span>
              </div>
            )}
          </div>
          <div className="store-profile-gallery-toolbar">
            <input
              type="file"
              id="storeCoverUpload"
              accept=".jpg,.jpeg,.png"
              className="store-profile-hidden-input"
              onChange={handleCoverImageUpload}
            />
            <label
              htmlFor="storeCoverUpload"
              className={`btn ${coverImageUrl ? 'ghost' : 'primary'} small${isUploadingCover ? ' disabled' : ''}`}
              aria-disabled={isUploadingCover}
            >
              {isUploadingCover ? 'Uploading cover...' : coverImageUrl ? 'Change Cover' : 'Upload Cover'}
            </label>
            {coverImageUrl ? (
              <button
                type="button"
                className={`btn ghost small danger${isDeletingCover ? ' disabled' : ''}`}
                onClick={handleDeleteCoverImage}
                disabled={isDeletingCover || isUploadingCover}
                aria-label="Remove cover photo"
              >
                {isDeletingCover ? 'Removing...' : 'Remove Cover'}
              </button>
            ) : null}
            <span className="store-profile-upload-hint">Recommended wide photo · Max 1MB · JPG / PNG</span>
          </div>
        </div>
      </div>
      <div className="card store-profile-card">
        <div className="store-profile-form-grid">
          <div className="store-profile-field-full store-profile-readiness-card" aria-live="polite">
            <div className="store-profile-readiness-header">
              <div>
                <h3>Customer-facing profile</h3>
                <p>Profiles with clear descriptions and highlights build more customer trust.</p>
              </div>
              <div className={`store-profile-readiness-badge ${readiness.completionLevel.toLowerCase()}`}>
                {readiness.completionLevel} · {readiness.completionScore}%
              </div>
            </div>
            <div className="store-profile-progress-track" aria-hidden="true">
              <span className="store-profile-progress-fill" style={{ width: `${readiness.completionScore}%` }} />
            </div>
            {readiness.missingItems.length > 0 ? (
              <ul className="store-profile-checklist">
                {readiness.missingItems.slice(0, 3).map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : (
              <p className="store-profile-checklist-complete">Your store profile is ready for customer-facing trust sections.</p>
            )}
          </div>
          <label className="field">
            <span className="label">Store Name *</span>
            <input
              type="text"
              name="storeName"
              className="input"
              value={formData.storeName}
              maxLength={50}
              onChange={handleChange}
              placeholder="Enter store name"
            />
          </label>
          <label className="field">
            <span className="label">Store Category</span>
            <select
              name="storeCategory"
              className="input"
              value={formData.storeCategory}
              onChange={handleChange}
            >
              {CATEGORY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="label">Phone</span>
            <input
              type="text"
              name="phone"
              className="input"
              value={formData.phone}
              maxLength={20}
              onChange={handleChange}
              placeholder="+91 98765 43210"
            />
          </label>
          <div className="field store-profile-field-full">
            <GoogleMapsProvider>
              <StoreLocationEditor
                pincode={formData.pincode}
                address={formData.address}
                latitude={formData.latitude}
                longitude={formData.longitude}
                onChange={handleLocationChange}
                onValidationChange={handleLocationValidationChange}
                disabled={isSaving}
                onError={(error) => onToast?.({ message: error, variant: 'error' })}
              />
            </GoogleMapsProvider>
          </div>
          <div className="field store-profile-field-full">
            <OperatingHoursSection
              weekdayOpen={formData.weekdayOpen}
              weekdayClose={formData.weekdayClose}
              weekendOpen={formData.weekendOpen}
              weekendClose={formData.weekendClose}
              disabled={isSaving}
              onTimeChange={handleTimeChange}
              footnote="Your store's operating hours. Appointment booking hours must stay within this window."
            />
          </div>
          <label className="field store-profile-field-full">
            <span className="label">Description</span>
            {legacyDescriptionNotice ? (
              <span className="store-profile-legacy-note">
                We found an unsaved description from an earlier session. Save your profile to keep it.
              </span>
            ) : null}
            <textarea
              name="description"
              className="input store-profile-description"
              value={formData.description}
              maxLength={320}
              onChange={handleChange}
              placeholder="Tell customers about your store."
              rows={4}
            />
          </label>
          <label className="field store-profile-field-full">
            <span className="label">Headline</span>
            <input
              type="text"
              name="headline"
              className="input"
              value={formData.headline}
              maxLength={80}
              onChange={handleChange}
              placeholder="Help customers understand why they should visit your store"
            />
          </label>
          <ProfileEntryListField
            name="signatureHighlights"
            label="Signature highlights"
            purpose="Use this for the services or strengths customers should notice first on your store page."
            example="Example: Bridal makeup specialists"
            entries={formData.signatureHighlights}
            maxItems={PROFILE_ENTRY_LIMITS.signatureHighlights}
            onEntryChange={handleProfileEntryChange}
            onAddEntry={handleAddProfileEntry}
            onRemoveEntry={handleRemoveProfileEntry}
          />
          <ProfileEntryListField
            name="valueProps"
            label="Value points"
            purpose="Use this for reasons customers should choose you, such as speed, convenience, price, or availability."
            example="Example: Walk-ins welcome on weekends"
            entries={formData.valueProps}
            maxItems={PROFILE_ENTRY_LIMITS.valueProps}
            onEntryChange={handleProfileEntryChange}
            onAddEntry={handleAddProfileEntry}
            onRemoveEntry={handleRemoveProfileEntry}
          />
          <label className="field store-profile-field-full">
            <span className="label">Store Code</span>
            <input
              type="text"
              name="storeCode"
              className="input"
              value={formData.storeCode}
              disabled
            />
          </label>
          <div className="store-profile-actions">
            <button
              type="button"
              className="btn ghost small"
              onClick={handleCancel}
              disabled={!hasChanges || isSaving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn primary small"
              onClick={handleSave}
              disabled={!canSave}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      <div className="card store-profile-card">
        <div className="store-profile-gallery-section">
          <div className="store-profile-gallery-header">
            <div>
              <h3>Gallery Photos</h3>
              <p>Manage the photos shown in the mobile app gallery.</p>
            </div>
          </div>
          <div className="field store-profile-field-full">
            <span className="label">Gallery Photos</span>
            <p className="store-profile-gallery-copy">Add up to 8 photos to show in the mobile gallery.</p>
            <div className="store-profile-gallery-toolbar">
              <input
                type="file"
                id="storeGalleryUpload"
                accept=".jpg,.jpeg,.png"
                multiple
                className="store-profile-hidden-input"
                onChange={handleGalleryUpload}
              />
              <label
                htmlFor="storeGalleryUpload"
                className={`btn ghost small${isUploadingGallery ? ' disabled' : ''}`}
                aria-disabled={isUploadingGallery}
              >
                {isUploadingGallery ? 'Uploading photos...' : 'Add Photos'}
              </label>
              <span className="store-profile-upload-hint">Current system stores original uploads. 4K conversion is not implemented in this release.</span>
            </div>
            {galleryItems.length > 0 ? (
              <div className="store-profile-gallery-grid">
                {galleryItems.map(({ url, isPreview }) => (
                  <div key={url} className={`store-profile-gallery-item${isPreview ? ' preview' : ''}`}>
                    <div className="store-profile-gallery-frame">
                      <img src={url} alt="Store gallery" className="store-profile-gallery-image" />
                      {isPreview ? <span className="store-profile-gallery-chip">Uploading preview</span> : null}
                    </div>
                    {!isPreview ? (
                      <button
                        type="button"
                        className="store-profile-gallery-remove"
                        onClick={() => handleRemoveGalleryImage(url)}
                        aria-label="Remove gallery image"
                      >
                        Remove
                      </button>
                    ) : (
                      <span className="store-profile-gallery-pending">Will be added after upload completes</span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="store-profile-gallery-empty">No gallery photos added yet.</div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default StoreProfile;
